import { memo } from "react";
import { FormattedMessage, FormattedPlural } from "react-intl";
import PropTypes from "prop-types";
import { Amount } from "app/components/ui/Amount/Amount";
import "./QuotationLuggageSelectLabel.scss";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { BAGGAGE_TYPES } from "app/constants";
import SavingsHighlight from "app/components/ui/SavingsHighlight/SavingsHighlight";

const LUGGAGE_TRANSLATION_MAP = {
	[BAGGAGE_TYPES.CABIN_BAGGAGE]: {
		singular: "quotation.luggage.cabin.singular.option",
		plural: "quotation.luggage.cabin.plural.option",
	},
	[BAGGAGE_TYPES.HAND_BAGGAGE]: {
		singular: "quotation.luggage.handbag.singular.option",
		plural: "quotation.luggage.handbag.plural.option",
	},
	[BAGGAGE_TYPES.TRAIN_BAGGAGE]: {
		singular: "quotation.transport.luggage.singular.option",
		plural: "quotation.transport.luggage.plural.option",
	},
	[BAGGAGE_TYPES.CHECKED_BAGGAGE]: {
		singular: "quotation.luggage.singular.option",
		plural: "quotation.luggage.plural.option",
	},
};

const getLuggageTranslationKeys = luggageType =>
	LUGGAGE_TRANSLATION_MAP[luggageType] ?? {
		singular: "quotation.luggage.singular.option",
		plural: "quotation.luggage.plural.option",
	};

const QuotationLuggageSelectLabel = ({
	quantity,
	price,
	isSelected,
	publicPrice,
	maxWeightPerBaggage,
	luggageType,
}) => {
	const isOffered = price === 0 && quantity > 0;
	const translationKeys = getLuggageTranslationKeys(luggageType);

	const discountedAmount = (publicPrice || 0) - (price || 0);

	return (
		<div className="quotation-luggage-select-label">
			<div className="quotation-luggage-select-label__info">
				<Typography
					variant={TYPOGRAPHY_VARIANTS.REGULAR}
					variantMD={TYPOGRAPHY_VARIANTS.LARGE}
					component="p"
					className="quotation-luggage-select-label__quantity"
				>
					<Typography
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						variantMD={TYPOGRAPHY_VARIANTS.LARGE}
						isBold
						component="span"
					>
						{quantity}x{" "}
					</Typography>

					<FormattedPlural
						value={quantity}
						one={<FormattedMessage id={translationKeys.singular} />}
						other={<FormattedMessage id={translationKeys.plural} />}
					/>
				</Typography>

				{!isSelected && maxWeightPerBaggage > 0 && (
					<Typography
						variant={TYPOGRAPHY_VARIANTS.SMALL}
						variantMD={TYPOGRAPHY_VARIANTS.REGULAR}
						className="quotation-luggage-select-label__max-weight"
						component="p"
					>
						<FormattedMessage
							id="quotation.transport.luggage.option.max.weight"
							values={{
								weight: maxWeightPerBaggage,
							}}
						/>
					</Typography>
				)}
			</div>

			{!isSelected && (
				<div className="quotation-luggage-select-label__price">
					{isOffered ? (
						<Typography
							className="quotation-luggage-select-label__price--offered"
							variant={TYPOGRAPHY_VARIANTS.REGULAR}
							isBold
							component="span"
						>
							<FormattedMessage id="general.offered" />
						</Typography>
					) : (
						<Amount sign="+" signIsBold={false} value={price} size="large" />
					)}

					{discountedAmount > 0 && (
						<SavingsHighlight
							messageKey="general.save.amount"
							amountSaved={discountedAmount}
							trackEvent="luggage_click_savings"
							trackData={{
								baggageCount: quantity,
							}}
						/>
					)}
				</div>
			)}
		</div>
	);
};

QuotationLuggageSelectLabel.propTypes = {
	quantity: PropTypes.number.isRequired,
	price: PropTypes.number.isRequired,
	isSelected: PropTypes.bool.isRequired,
	publicPrice: PropTypes.number,
	maxWeightPerBaggage: PropTypes.number,
	luggageType: PropTypes.oneOf(Object.values(BAGGAGE_TYPES)),
};

export default memo(QuotationLuggageSelectLabel);
